import { Outlet, useLocation, Navigate } from 'react-router'
import { routes } from '../routes'
import LanguageSelector from './LanguageSelector'
import Seo from './Seo'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'

// Feature flag: the Oleshky rally tab and the mobile hamburger menu show by default.
// Set localStorage.enableOleshkyNav = 'false' to see the old navbar without them.
function isOleshkyNavEnabled() {
  try {
    return localStorage.getItem('enableOleshkyNav') !== 'false'
  } catch {
    return true
  }
}

export function Layout() {
  const { t } = useTranslation()
  const location = useLocation()
  const search = location.search || ''
  const [showOleshky] = useState(isOleshkyNavEnabled)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // close the mobile menu on any click outside it (e.g. on the language selector)
  useEffect(() => {
    if (!menuOpen) return
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [menuOpen])

  if (location.pathname.startsWith('/do-more')) {
    return <Navigate to={`${routes.witnesses.path}${search}`} replace />
  }

  const tabClass = (path: string) => {
    const isActive = location.pathname.startsWith(path)
    return `py-2 md:px-10 lowercase border border-white hover:bg-blue-50 text-center${
      isActive ? ' text-blue-700' : ''
    }`
  }

  const tabs = [
    ...(showOleshky ? [{ path: routes.oleshky.path, label: 'navbar.oleshky' }] : []),
    { path: routes.help.path, label: 'navbar.help' },
    { path: routes.leftBank.path, label: 'navbar.leftBank' },
    { path: routes.action.path, label: 'navbar.action' },
  ]

  return (
    <div className='min-h-screen w-full text-gray-900 flex flex-col text-lg'>
      <Seo />
      <nav className='w-full flex justify-center'>
        <div className='relative w-full lg:max-w-5xl flex items-center px-4'>
          <div
            className={`flex gap-4 ${showOleshky ? 'flex-1 min-w-0' : 'w-full'} justify-evenly font-bold align-middle text-center items-center`}
          >
            {tabs.map((tab, i) => (
              // on tablet and smaller, only the first tab stays; the rest move into the menu
              <a
                key={tab.path}
                href={`${tab.path}${search}`}
                className={`${tabClass(tab.path)}${showOleshky && i > 0 ? ' hidden lg:block' : ''}`}
              >
                {t(tab.label)}
              </a>
            ))}
            {showOleshky && (
              <div ref={menuRef} className='relative lg:hidden'>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-label={t('navbar.menu')}
                  aria-expanded={menuOpen}
                  className='flex flex-col justify-center gap-1.5 px-2 py-2 hover:bg-blue-50 cursor-pointer'
                >
                  <span className='block w-6 h-0.5 bg-gray-900'></span>
                  <span className='block w-6 h-0.5 bg-gray-900'></span>
                  <span className='block w-6 h-0.5 bg-gray-900'></span>
                </button>
                {menuOpen && (
                  <div className='absolute left-1/2 -translate-x-1/2 mt-2 bg-white shadow-xl p-2 z-50 flex flex-col min-w-48'>
                    {tabs.map((tab) => (
                      <a
                        key={tab.path}
                        href={`${tab.path}${search}`}
                        className={`${tabClass(tab.path)} px-4 whitespace-nowrap`}
                      >
                        {t(tab.label)}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* <a
              href={`${routes.rallies.path}${search}`}
              className='py-2 md:px-10 lowercase border border-white hover:bg-blue-50 text-center'
            >
              {t('navbar.rallies')}
            </a> */}
          </div>
          <div
            className={
              showOleshky
                ? 'shrink-0 flex items-center lg:absolute lg:inset-y-0 lg:right-4'
                : 'absolute inset-y-0 right-4 flex items-center'
            }
          >
            <LanguageSelector />
          </div>
        </div>
      </nav>
      <main>
        <Outlet />
      </main>
      <footer className='mt-auto w-full text-center text-md pt-16 pb-32 px-6 bg-gray-200 space-y-4'>
        <p className='text-xl'>
          {t('footer.contact')}{' '}
          <a className='text-blue-700 underline' href={`mailto:${t('help.actionEmail')}`}>
            {t('help.actionEmail')}
          </a>
        </p>
      </footer>
    </div>
  )
}
